import { TreeItems } from 'dnd-kit-sortable-tree';
import { useRef, useState } from 'react';
import { ProjectElement } from 'services/api/api-client.types';
import { toProjectElements, updateFolderProperties } from './documentsObserver';
import { BaseNode, cloneNodes } from './types/base';
import { showErrorToast } from 'components/uikit/toast/showToast';

export function useDocumentTree<T extends BaseNode>({
  onInvalidate,
  onPatch,
}: {
  onPatch: (cleanItems: ProjectElement[]) => Promise<void>;
  onInvalidate: () => Promise<void>;
}) {
  const [items, setItems] = useState<TreeItems<T>>([]);
  const itemsRef = useRef(items);

  const bufferedItems = useRef<TreeItems<T> | null>(null);

  itemsRef.current = items;

  const undo = () => {
    if (bufferedItems.current) {
      setItems(bufferedItems.current);
      bufferedItems.current = null;
    }
  };

  const patchItems = async (itemsToPatch: TreeItems<BaseNode>) => {
    const cleanItems = toProjectElements(cloneNodes(itemsToPatch));
    bufferedItems.current = items;
    try {
      await onPatch(cleanItems);
      bufferedItems.current = null;
      return true;
    } catch (e: any) {
      showErrorToast(e);
      undo();
      return false;
    }
  };

  const onChangeItems = async (
    changedItems: TreeItems<T>,
    saveInDb: boolean = true,
  ) => {
    updateFolderProperties(changedItems);
    setItems([...changedItems]);
    if (saveInDb) return await patchItems(changedItems);
    return false;
  };

  const onChangeItemsWithInvalidation = async (changedItems: TreeItems<T>) => {
    if (await patchItems(changedItems)) {
      await onInvalidate();
      return true;
    }
    return false;
  };

  return {
    items,
    setItems,
    itemsRef,
    onChangeItems,
    onChangeItemsWithInvalidation,
  };
}
